import { useQuery } from "@apollo/client";
import {
  BaseTable,
  Button,
  Flexbox,
  IBaseTableProps,
  Modal
} from "@sede-x/shell-ds-react-framework";
import { ColumnDef, getPaginationRowModel } from "@tanstack/react-table";
import calculateSize from "calculate-size";
import dayjs from "dayjs";
import { loader } from "graphql.macro";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import LoadingPanel from "shared/components/LoadingPanel";
import { GqlResponse, TMovementRevision } from "ticketing/ticketing.types";
import { formatDateForDisplay, formatNumber } from "ticketing/utils";
import "./movements.css";
import RevisionsMovementAlerts from "./RevisionsMovementAlerts";
import RevisionsMovementAllocations from "./RevisionsMovementAllocations";
import RevisionsMovementDeliveryEvents from "./RevisionsMovementDeliveryEvents";
import RevisionsMovementMeasures from "./RevisionsMovementMeasures";
import RevisionsMovementPnlDetails from "./RevisionsMovementPnlDetails";
import RevisionsMovementTickets from "./RevisionsMovementTickets";

const PADDING = 40;
const FONT_OPTIONS = {
  font: "14px Segoe UI",
  fontWeight: "normal"
};
const minWidth = 80;
const maxAllowedWidth = 400;

const propertyGetter = (path: string) => {
  return <T extends TMovementRevisionTransformed>(obj: T): unknown => {
    return path.split(".").reduce((prev: unknown, curr: string) => {
      return prev && typeof prev === "object" ? (prev as Record<string, unknown>)[curr] : null;
    }, obj as unknown);
  };
};

type Response = GqlResponse<TMovementRevision[], "movementRevisions">;

const GET_MOVEMENT_REVISIONS = loader("../../ticketing-graphql/movementRevisions.graphql");

type TMovementRevisionTransformed = Omit<
  TMovementRevision,
  | "actualizationComplete"
  | "lastModifiedBy"
  | "readyToInvoice"
  | "scheduledQuantity"
  | "actualizedQuantity"
> & {
  actualizationComplete: string;
  readyToInvoice: string;
  lastModifiedBy: string;
  scheduledQuantity: string;
  actualizedQuantity: string;
};

const RevisionActionButton = (
  revisionNumber: string,
  onClick: (revisionNumber: string) => void,
  label: string
) => {
  return (
    <Button
      size="small"
      style={{ borderRadius: "5px" }}
      onClick={() => onClick(revisionNumber)}>
      {label}
    </Button>
  );
};
const MAX_MODAL_HEIGHT = 600; // Maximum height for the modal
const MIN_MODAL_HEIGHT = 200;

const transformData = (revisions: TMovementRevision[]) => {
  return revisions
    ?.map(rev => ({
      ...rev,
      startDate: new Date(`${rev?.startDate}Z`),
      revisionDateTime: new Date(`${rev?.revisionDateTime}Z`),
      actualizationComplete: rev?.actualizationComplete ? "Yes" : "No",
      readyToInvoice: rev?.actualizationComplete || rev?.readyToInvoice ? "Yes" : "No",
      actualizedQuantity: rev.actualizedQuantity
        ? `${formatNumber(rev.actualizedQuantity)} ${rev.unitOfMeasure?.name}`
        : "",
      scheduledQuantity: rev.scheduledQuantity
        ? `${formatNumber(rev.scheduledQuantity)} ${rev.unitOfMeasure?.name}`
        : "",
      lastModifiedBy: [rev.lastModifiedBy.lastName, rev.lastModifiedBy.firstName]
        .filter(Boolean)
        .join(", ")
    }))
    .sort((a, b) => b.revisionNumber.localeCompare(a.revisionNumber));
};

type MovementRevisionsProps = {
  movementId: string;
  enterpriseSystemCode: string;
  onClose: () => void;
};

const RevisionsMovement = ({
  movementId,
  onClose,
  enterpriseSystemCode
}: MovementRevisionsProps) => {
  const { loading, error } = useQuery<Response>(GET_MOVEMENT_REVISIONS, {
    fetchPolicy: "no-cache",
    variables: { id: movementId },
    onCompleted: data => setTransformedData(transformData(data.movementRevisions))
  });

  const [transformedData, setTransformedData] = useState<TMovementRevisionTransformed[]>([]);

  const [showAllocations, setShowAllocations] = useState<boolean>(false);
  const [showTickets, setShowTickets] = useState<boolean>(false);
  const [showDeliveryEvents, setShowDeliveryEvents] = useState<boolean>(false);
  const [showMeasures, setShowMeasures] = useState<boolean>(false);
  const [showAlerts, setShowAlerts] = useState<boolean>(false);
  const [showPnlDetails, setShowPnlDetails] = useState<boolean>(false);
  const [revisionNumber, setRevisionNumber] = useState<string>();

  const calculateWidth = useCallback(
    (field: string, title: string, format?: string) => {
      let maxWidth = calculateSize(title, FONT_OPTIONS).width + PADDING;

      if (transformedData && field) {
        transformedData.forEach(item => {
          // Get the value using our property getter
          const getValue = propertyGetter(field);
          const value = getValue(item);

          const formattedValue =
            value instanceof Date && format ? formatDateForDisplay(value) : String(value);

          const contentWidth = calculateSize(formattedValue, FONT_OPTIONS).width + PADDING;
          if (contentWidth > maxWidth) {
            maxWidth = contentWidth;
          }
        });
      }
      const finalWidth = maxWidth + 10;

      return Math.min(Math.max(finalWidth, minWidth), maxAllowedWidth);
    },
    [transformedData]
  );

  const columns = useMemo<ColumnDef<TMovementRevisionTransformed, string>[]>(
    () => [
      {
        header: "Revision #",
        accessorKey: "revisionNumber",
        size: calculateWidth("revisionNumber", "Revision #")
      },
      {
        header: "Movement Date",
        accessorKey: "startDate",
        cell: props => dayjs(props.row.original.startDate).format("MM/DD/YY"),
        size: calculateWidth("startDate", "Movement Date", "MM/DD/YY")
      },
      {
        header: "Status",
        accessorKey: "status.name",
        size: calculateWidth("status.name", "Status")
      },
      {
        header: "Product",
        accessorKey: "product.name",
        size: calculateWidth("product.name", "Product")
      },
      {
        header: "Location",
        accessorKey: "titleTransferFacility.name",
        size: calculateWidth("titleTransferFacility.name", "Location")
      },
      {
        header: "Nom Activity",
        accessorKey: "activityType.name",
        size: calculateWidth("activityType.name", "Nom Activity")
      },
      {
        header: "Scheduled Qty",
        accessorKey: "scheduledQuantity",
        size: calculateWidth("scheduledQuantity", "Scheduled Qty")
      },
      {
        header: "Actualized Qty",
        accessorKey: "actualizedQuantity",
        size: calculateWidth("actualizedQuantity", "Actualized Qty")
      },
      {
        header: "Updated At",
        accessorKey: "revisionDateTime",
        cell: props => dayjs(props.row.original.revisionDateTime).format("MM/DD/YY HH:mm:ss"),
        size: calculateWidth("revisionDateTime", "Updated At", "MM/DD/YY HH:mm:ss")
      },
      {
        header: "Updated By",
        accessorKey: "lastModifiedBy",
        size: calculateWidth("lastModifiedBy", "Updated By")
      },
      {
        header: "Act Complete",
        accessorKey: "actualizationComplete",
        size: calculateWidth("actualizationComplete", "Act Complete")
      },
      {
        header: "Ready To Invoice",
        accessorKey: "readyToInvoice",
        size: calculateWidth("readyToInvoice", "Ready To Invoice")
      },
      {
        header: "Allocations",
        cell: props =>
          RevisionActionButton(
            props.row.original.revisionNumber,
            handleAllocation,
            "Allocations"
          ),
        size: calculateWidth("allocations", "Allocations")
      },
      {
        header: "Tickets",
        cell: props =>
          RevisionActionButton(props.row.original.revisionNumber, handleTicket, "Tickets"),
        size: calculateWidth("tickets", "Tickets")
      },
      {
        header: "Delivery Events",
        cell: props =>
          RevisionActionButton(
            props.row.original.revisionNumber,
            handleDeliveryEvents,
            "Delivery Events"
          ),
        size: calculateWidth("deliveryEvents", "Delivery Events")
      },
      {
        header: "Measures",
        cell: props =>
          RevisionActionButton(props.row.original.revisionNumber, handleMeasures, "Measures"),
        size: calculateWidth("measures", "Measures")
      },
      {
        header: "Alerts",
        cell: props =>
          RevisionActionButton(props.row.original.revisionNumber, handleAlert, "Alerts"),
        size: calculateWidth("alerts", "Alerts")
      },
      {
        header: "Pnl Details",
        cell: props =>
          RevisionActionButton(
            props.row.original.revisionNumber,
            handlePnlDetails,
            "Pnl Details"
          ),
        size: calculateWidth("pnlDetails", "PNL Details")
      }
    ],
    [calculateWidth]
  );

  const handleAllocation = (revNumber: string) => {
    setRevisionNumber(revNumber);
    setShowAllocations(true);
  };

  const handleTicket = (revNumber: string) => {
    setRevisionNumber(revNumber);
    setShowTickets(true);
  };

  const handleDeliveryEvents = (revNumber: string) => {
    setRevisionNumber(revNumber);
    setShowDeliveryEvents(true);
  };

  const handleAlert = (revNumber: string) => {
    setRevisionNumber(revNumber);
    setShowAlerts(true);
  };

  const handlePnlDetails = (revNumber: string) => {
    setRevisionNumber(revNumber);
    setShowPnlDetails(true);
  };

  const handleMeasures = (revNumber: string) => {
    setRevisionNumber(revNumber);
    setShowMeasures(true);
  };

  const tableOptions: IBaseTableProps<TMovementRevisionTransformed>["tableOptions"] = {
    defaultColumn: {
      minSize: 100,
      maxSize: 400
    },
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    getPaginationRowModel: getPaginationRowModel()
  };

  const deliveryIdTitle = `Movement Revisions: Delivery ID - ${enterpriseSystemCode}`;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [modalHeight, setModalHeight] = useState<number | undefined>(undefined);

  // Add an effect to measure and adjust the height
  useEffect(() => {
    if (!loading && transformedData && tableContainerRef.current) {
      setTimeout(() => {
        if (tableContainerRef.current) {
          // Calculate the content height
          const tableHeight = tableContainerRef.current.scrollHeight;

          const headerHeight = 30;
          const padding = 40;

          const calculatedHeight = Math.min(
            MAX_MODAL_HEIGHT,
            Math.max(MIN_MODAL_HEIGHT, tableHeight + headerHeight + padding)
          );

          setModalHeight(calculatedHeight);
        }
      }, 100);
    }
  }, [transformedData, loading]);

  return (
    <Modal
      title={deliveryIdTitle}
      bodyPadding={false}
      height={modalHeight ?? 600}
      onClose={onClose}
      width={1200}
      open={true}
      maskClosable={false}
      className="movement-revisions">
      {showAllocations && revisionNumber && (
        <Modal
          open={showAllocations && !!revisionNumber}
          bodyPadding={false}
          width={"1000px"}
          title={`Revision Allocations: ${deliveryIdTitle}`}
          onClose={() => setShowAllocations(false)}>
          <div
            style={{
              flex: 1,
              overflow: "auto"
            }}>
            <RevisionsMovementAllocations
              movementId={movementId}
              revisionNumber={revisionNumber}
            />
          </div>
        </Modal>
      )}
      {showTickets && revisionNumber && (
        <Modal
          bodyPadding={false}
          open={showTickets && !!revisionNumber}
          width={"1000px"}
          title={`Revision Tickets: ${deliveryIdTitle}`}
          onClose={() => setShowTickets(false)}>
          <RevisionsMovementTickets
            movementId={movementId}
            revisionNumber={revisionNumber}></RevisionsMovementTickets>
        </Modal>
      )}
      {showAlerts && revisionNumber && (
        <Modal
          bodyPadding={false}
          open={showAlerts && !!revisionNumber}
          width={"1000px"}
          title={`Revision Alerts: ${deliveryIdTitle}`}
          onClose={() => setShowAlerts(false)}>
          <RevisionsMovementAlerts
            movementId={movementId}
            revisionNumber={revisionNumber}></RevisionsMovementAlerts>
        </Modal>
      )}
      {showPnlDetails && revisionNumber && (
        <Modal
          bodyPadding={false}
          open={true}
          width={"1000px"}
          title={`Revision Pnl Details: ${deliveryIdTitle}`}
          onClose={() => setShowPnlDetails(false)}>
          <RevisionsMovementPnlDetails
            movementId={movementId}
            revisionNumber={revisionNumber}></RevisionsMovementPnlDetails>
        </Modal>
      )}
      {showMeasures && revisionNumber && (
        <Modal
          bodyPadding={false}
          open={showMeasures && !!revisionNumber}
          width={"1000px"}
          title={`Revision Measures: ${deliveryIdTitle}`}
          onClose={() => setShowMeasures(false)}>
          <RevisionsMovementMeasures
            movementId={movementId}
            revisionNumber={revisionNumber}></RevisionsMovementMeasures>
        </Modal>
      )}
      {showDeliveryEvents && revisionNumber && (
        <Modal
          bodyPadding={false}
          open={showDeliveryEvents && !!revisionNumber}
          width={"1000px"}
          title={`Revision DeliveryEvents: ${deliveryIdTitle}`}
          onClose={() => setShowDeliveryEvents(false)}>
          <RevisionsMovementDeliveryEvents
            movementId={movementId}
            revisionNumber={revisionNumber}></RevisionsMovementDeliveryEvents>
        </Modal>
      )}

      <Flexbox
        flexDirection="column"
        ref={tableContainerRef}
        style={{
          flex: 1,
          overflow: "auto"
        }}>
        {loading && <LoadingPanel />}
        {error && <ApolloErrorViewer error={error} />}
        <BaseTable
          data={transformedData}
          columns={columns}
          tableOptions={tableOptions}
          style={{ width: "100%", tableLayout: "fixed" }}
          useColumnsSizing={true}
          noFooter={true}
        />
      </Flexbox>
    </Modal>
  );
};

export default RevisionsMovement;
